import { observer } from 'mobx-react-lite'
import { Alert, Checkbox, EnumDropdownItemVariant, type IDropdownItem, Typography } from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { RadioOptions } from 'shared/ui/Radio'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { UserCardContent } from 'entities/Users'
import { type DeleteMemberStore } from 'pages/settings/pages/members/ui/actions/deleteMember/ui/DeleteMemberModal/store/DeleteMemberStore'
import styles from './styles.module.scss'

type IDeleteMemberModalProps = {
  store: DeleteMemberStore
}

export const DeleteMemberModal = observer(({ store }: IDeleteMemberModalProps) => {
  const {
    memberToDelete,
    hasPaidSeat,
    hasInboxOwnership,
    deleteSeatOption,
    deleteConfirmCheckbox,
    reassignContactsUserId,
    reassignConversationsUserId,
    reassignInboxOwnershipUserId,
    availableMembersForReassignment,
    seatOptions,
    showSeatOption,
    onSetSeatOption,
    onSetDeleteConfirmCheckbox,
    onSetReassignContactsUserId,
    onSetReassignConversationsUserId,
    onSetReassignInboxOwnershipUserId,
  } = store

  if (!memberToDelete) return null

  const memberOptions: IDropdownItem[] = availableMembersForReassignment.map((user) => ({
    id: user.id,
    label: user.name,
    variant: EnumDropdownItemVariant.Custom,
    renderOption: () => <UserCardContent item={user} />,
  }))

  const selectedContactsUser = availableMembersForReassignment.find(
    (user) => user.id === reassignContactsUserId
  )
  const selectedConversationsUser = availableMembersForReassignment.find(
    (user) => user.id === reassignConversationsUserId
  )
  const selectedInboxOwnershipUser = availableMembersForReassignment.find(
    (user) => user.id === reassignInboxOwnershipUserId
  )

  return (
    <>
      <Alert
        item={{
          type: 'alert',
          title: `Are you sure you want to delete ${memberToDelete.fullName}?`,
          desc: 'This user will permanently lose access to this workspace and all associated data. This action cannot be undone.',
        }}
        marginBottom={24}
      />
      <div className={styles.content}>
        {hasPaidSeat && (
          <>
            <SelectField
              size={'medium'}
              labelText={'Reassign owned contacts to'}
              value={reassignContactsUserId ?? undefined}
              setValue={onSetReassignContactsUserId}
              items={memberOptions}
              selectProps={{
                placeholder: 'Select member',
              }}
              btnProps={{
                placeholder: 'Select member',
                text: selectedContactsUser ? (
                  <UserCardContent item={selectedContactsUser} />
                ) : (
                  'Select member'
                ),
                style: {
                  height: '40px',
                  color: !selectedContactsUser ? 'var(--content-neutral-primary)' : undefined,
                },
              }}
              withSearch={true}
            />

            <SelectField
              size={'medium'}
              labelText={'Reassign conversations to'}
              labelProps={{
                questionTooltipProps: {
                  label: 'Inboxes, AI Agents, Workflows, Campaigns, Keywords, Triggers',
                  width: 212,
                },
              }}
              value={reassignConversationsUserId ?? undefined}
              setValue={onSetReassignConversationsUserId}
              items={memberOptions}
              selectProps={{
                placeholder: 'Select member',
              }}
              btnProps={{
                placeholder: 'Select member',
                text: selectedConversationsUser ? (
                  <UserCardContent item={selectedConversationsUser} />
                ) : (
                  'Select member'
                ),
                style: {
                  height: '40px',
                  color: !selectedConversationsUser ? 'var(--content-neutral-primary)' : undefined,
                },
              }}
              withSearch={true}
            />

            {hasInboxOwnership && (
              <SelectField
                size={'medium'}
                labelText={'Reassign inbox ownership to'}
                value={reassignInboxOwnershipUserId ?? undefined}
                setValue={onSetReassignInboxOwnershipUserId}
                items={memberOptions}
                selectProps={{
                  placeholder: 'Select member',
                }}
                btnProps={{
                  placeholder: 'Select member',
                  text: selectedInboxOwnershipUser ? (
                    <UserCardContent item={selectedInboxOwnershipUser} />
                  ) : (
                    'Select member'
                  ),
                  style: {
                    height: '40px',
                    color: !selectedInboxOwnershipUser
                      ? 'var(--content-neutral-primary)'
                      : undefined,
                  },
                }}
                withSearch={true}
              />
            )}

            {showSeatOption && (
              <div className={styles.section}>
                <Label
                  label={{
                    text: 'What would you like to do with this seat?',
                  }}
                  marginBottom={4}
                />
                <RadioOptions
                  options={seatOptions}
                  active={deleteSeatOption}
                  onChange={onSetSeatOption}
                />
              </div>
            )}
          </>
        )}

        <label className={styles.wrapCheckbox} htmlFor={'deleteMemberCheckbox'}>
          <Checkbox
            id={'deleteMemberCheckbox'}
            checked={deleteConfirmCheckbox}
            onChange={onSetDeleteConfirmCheckbox}
            size={16}
            style={{ cursor: 'pointer' }}
          />
          <Typography
            variant={'text-input-field'}
            color={'tertiary'}
            ariaLabel={'delete_understanding'}
          >
            I understand this action is permanent and cannot be undone
          </Typography>
        </label>
      </div>
    </>
  )
})
